/**
 * <charm-cart> — Charmette-style cart conversion logic.
 * v23+: optimistic UI, no element replacement, single round-trip per action.
 */
(function () {
  if (customElements.get('charm-cart')) return;

  const TIMER_KEY = 'charm_cart_timer_started';
  const TIMER_DURATION_MIN = 15;

  class CharmCart extends HTMLElement {
    constructor() {
      super();
      this._onQty = this._onQty.bind(this);
      this._onRemove = this._onRemove.bind(this);
      this._onToggle = this._onToggle.bind(this);
      this._onGiftVariantChange = this._onGiftVariantChange.bind(this);
      this._onUpsellVariantChange = this._onUpsellVariantChange.bind(this);
      this._tickTimer = this._tickTimer.bind(this);
      this._qtyDebounce = null;
      this._pending = false; // single-flight guard
      this._queued = null;   // queued action while pending
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.isDrawer = this.dataset.isDrawer === 'true';
      this.giftThreshold = parseInt(this.dataset.giftThreshold || '10', 10);
      this.giftVariantId = this.dataset.giftVariantId || null;

      this._bindEvents();
      this._startTimer();
      // Reconcile gift on load - non-blocking, no UI update needed (already rendered correctly)
      setTimeout(() => this._reconcileGiftSilent(), 100);
    }

    disconnectedCallback() {
      if (this.timerInterval) clearInterval(this.timerInterval);
    }

    _bindEvents() {
      // Use event delegation — works even after partial DOM updates
      this.addEventListener('click', (e) => {
        const qtyBtn = e.target.closest('[data-qty-change]');
        if (qtyBtn) return this._onQty({ currentTarget: qtyBtn });

        const removeBtn = e.target.closest('[data-remove-line]');
        if (removeBtn) return this._onRemove({ currentTarget: removeBtn });

        const toggleBtn = e.target.closest('[data-upsell-toggle]');
        if (toggleBtn) return this._onToggle({ currentTarget: toggleBtn });
      });

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-qty-input]')) return this._onQty({ currentTarget: e.target });
        if (e.target.matches('[data-gift-variant]')) return this._onGiftVariantChange({ currentTarget: e.target });
        if (e.target.matches('[data-upsell-variant]')) return this._onUpsellVariantChange({ currentTarget: e.target });
      });
    }

    // ---------- TIMER ----------
    _startTimer() {
      const display = this.querySelector('[data-timer-display]');
      if (!display) return;
      let startedAt = parseInt(localStorage.getItem(TIMER_KEY), 10);
      const now = Date.now();
      if (!startedAt || now - startedAt > TIMER_DURATION_MIN * 60 * 1000) {
        startedAt = now;
        localStorage.setItem(TIMER_KEY, String(startedAt));
      }
      this.timerStartedAt = startedAt;
      this._tickTimer();
      this.timerInterval = setInterval(this._tickTimer, 1000);
    }

    _tickTimer() {
      const display = this.querySelector('[data-timer-display]');
      if (!display) return;
      const elapsed = Math.floor((Date.now() - this.timerStartedAt) / 1000);
      let remaining = TIMER_DURATION_MIN * 60 - elapsed;
      if (remaining <= 0) {
        this.timerStartedAt = Date.now();
        localStorage.setItem(TIMER_KEY, String(this.timerStartedAt));
        remaining = TIMER_DURATION_MIN * 60;
      }
      const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
      const ss = String(remaining % 60).padStart(2, '0');
      display.textContent = `${mm}:${ss}`;
    }

    // ---------- CORE CART OPS ----------
    async _doCartAction(url, body, opts = {}) {
      // Single-flight: queue if a request is already in-flight
      if (this._pending) {
        this._queued = { url, body, opts };
        return null;
      }
      this._pending = true;

      let data = null;
      try {
        body.sections = [this.sectionId, 'cart-icon-bubble'].join(',');
        body.sections_url = window.location.pathname;

        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        data = await res.json();
        if (!opts.skipUpdate) this._applyResponse(data);
      } catch (e) {
        console.error('[charm-cart]', e);
      } finally {
        this._pending = false;
        if (this._queued) {
          const next = this._queued;
          this._queued = null;
          this._doCartAction(next.url, next.body, next.opts);
        } else if (!opts.skipUpdate && !this._reconciling) {
          // Reconcile the free gift AFTER _pending is cleared and no work is queued.
          // (Doing it inside _applyResponse would hit the _pending guard and no-op.)
          this._reconcileGiftSilent();
        }
      }
      return data;
    }

    _applyResponse(data) {
      if (!data || !data.sections) return;

      // Update cart bubble
      const bubbleHtml = data.sections['cart-icon-bubble'];
      if (bubbleHtml) {
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          const tmp = document.createElement('div');
          tmp.innerHTML = bubbleHtml;
          const fresh = tmp.querySelector('#cart-icon-bubble');
          if (fresh) bubble.innerHTML = fresh.innerHTML;
        }
      }

      // Update drawer/cart inner content WITHOUT replacing the <charm-cart> element itself.
      // This preserves event listeners and avoids losing UI state.
      const sectionHtml = data.sections[this.sectionId];
      if (sectionHtml) {
        const tmp = document.createElement('div');
        tmp.innerHTML = sectionHtml;
        const fresh = tmp.querySelector('charm-cart');
        if (fresh) {
          // Replace inner content only, not the wrapper
          this.innerHTML = fresh.innerHTML;
          // Update data attributes (gift variant might have changed)
          if (fresh.dataset.giftVariantId) {
            this.dataset.giftVariantId = fresh.dataset.giftVariantId;
            this.giftVariantId = fresh.dataset.giftVariantId;
          }
          // Re-attach timer (innerHTML wiped the display element)
          this._stopTimerInterval();
          this._startTimer();
        }
      }
      // Note: gift reconcile is triggered from _doCartAction's finally block
      // (after _pending clears) and once from connectedCallback on first load.
    }

    _stopTimerInterval() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    }

    // ---------- FREE GIFT (in-house auto-add; bracelet must be a €0 product) ----------
    // Adds the free bracelet to the cart when non-gift qty >= threshold, removes it below.
    // Customer picks the variant via the gift-bar dropdown (_onGiftVariantChange).
    //
    // Gift lines are identified by the _free_gift property (NOT by variant id), so:
    //  - a manually-purchased bracelet is never miscounted as the gift
    //  - any leftover/duplicate gift lines (e.g. after a variant swap) are collapsed to one
    //  - the function is fully idempotent and self-healing on every render
    //
    // IMPORTANT: disable "Buy Now / dynamic checkout" on charm products so customers
    // cannot bypass the cart. Cart-level express buttons are fine (gift already in cart).
    async _reconcileGiftSilent() {
      if (!this.giftVariantId || this.giftVariantId === '0') return;
      if (this._reconciling) return;
      this._reconciling = true;
      try {
        const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' }})).json();
        const giftId = parseInt(this.giftVariantId, 10);

        const giftLines = cart.items.filter((it) => it.properties && it.properties._free_gift === 'true');
        const nonGiftQty = cart.items
          .filter((it) => !(it.properties && it.properties._free_gift === 'true'))
          .reduce((s, it) => s + it.quantity, 0);
        const shouldHaveGift = nonGiftQty >= this.giftThreshold;

        // Determine the SINGLE mutation needed (if any), then run it through the
        // proven _doCartAction path so its response refreshes the UI like qty changes do.
        if (!shouldHaveGift) {
          // Remove the first gift line if present (rare duplicates handled on next pass).
          if (giftLines.length > 0) {
            await this._doCartActionRaw('/cart/change.js', { id: giftLines[0].key, quantity: 0 });
          }
        } else {
          const correct = giftLines.filter((gl) => gl.variant_id === giftId && gl.quantity === 1);
          const wrong = giftLines.filter((gl) => !(gl.variant_id === giftId && gl.quantity === 1));
          if (correct.length === 0 && wrong.length === 0) {
            // Nothing yet: add the chosen variant.
            await this._doCartActionRaw('/cart/add.js', { id: giftId, quantity: 1, properties: { _free_gift: 'true' } });
          } else if (correct.length === 0 && wrong.length > 0) {
            // Wrong variant in cart: remove it AND add the right one in one refresh.
            // Remove all wrong gift lines first (no UI refresh), then add + refresh.
            for (const gl of wrong) {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: gl.key, quantity: 0 }),
              });
            }
            await this._doCartActionRaw('/cart/add.js', { id: giftId, quantity: 1, properties: { _free_gift: 'true' } });
          } else if (wrong.length > 0 || correct.length > 1) {
            // Have the correct one but also extras/wrong: clean up extras.
            const toRemove = [...wrong, ...correct.slice(1)];
            for (let i = 0; i < toRemove.length - 1; i++) {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: toRemove[i].key, quantity: 0 }),
              });
            }
            // Last removal refreshes UI.
            if (toRemove.length > 0) {
              await this._doCartActionRaw('/cart/change.js', { id: toRemove[toRemove.length - 1].key, quantity: 0 });
            }
          }
          // else: exactly one correct line, nothing to do.
        }
      } catch (e) { /* non-fatal */ } finally {
        this._reconciling = false;
      }
    }

    // Like _doCartAction but ALWAYS applies the response (refreshes UI) and never
    // re-triggers reconcile (avoids loops). Used by the gift reconcile.
    async _doCartActionRaw(url, body) {
      try {
        body.sections = [this.sectionId, 'cart-icon-bubble'].join(',');
        body.sections_url = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/');
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        // The POST response includes the requested sections; apply them.
        if (data && data.sections) {
          this._applyResponse(data);
        }
      } catch (e) { console.error('[charm-cart] gift action failed', e); }
    }

    // ---------- HANDLERS ----------
    _onQty(event) {
      const target = event.currentTarget;
      const line = parseInt(target.dataset.line, 10);
      let qty;
      if (target.dataset.qtyChange != null) {
        const input = this.querySelector(`[data-qty-input][data-line="${line}"]`);
        const delta = parseInt(target.dataset.qtyChange, 10);
        qty = Math.max(0, (parseInt(input.value, 10) || 0) + delta);
        if (input) input.value = qty; // immediate visual update
      } else {
        qty = Math.max(0, parseInt(target.value, 10) || 0);
      }
      // Debounce rapid clicks
      clearTimeout(this._qtyDebounce);
      this._qtyDebounce = setTimeout(() => {
        this._doCartAction('/cart/change.js', { line, quantity: qty });
      }, 300);
    }

    _onRemove(event) {
      const line = parseInt(event.currentTarget.dataset.line, 10);
      // Optimistic: hide the row immediately
      const row = event.currentTarget.closest('.charm-cart-item');
      if (row) row.style.opacity = '0.5';
      this._doCartAction('/cart/change.js', { line, quantity: 0 });
    }

    _onToggle(event) {
      const btn = event.currentTarget;
      if (btn.disabled) return;
      const isOn = btn.getAttribute('aria-pressed') === 'true';
      const variantId = parseInt(btn.dataset.variantId, 10);
      if (!variantId) return;

      // OPTIMISTIC: flip the toggle visually immediately
      btn.setAttribute('aria-pressed', isOn ? 'false' : 'true');
      btn.disabled = true; // brief lock to prevent double-click

      // Re-enable after a short timeout regardless of network
      setTimeout(() => { btn.disabled = false; }, 800);

      if (isOn) {
        // Remove: we need the line, find it via cart.js (fast endpoint)
        this._removeVariant(variantId);
      } else {
        // Add: single round-trip
        this._doCartAction('/cart/add.js', { id: variantId, quantity: 1 });
      }
    }

    async _removeVariant(variantId) {
      try {
        const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' }})).json();
        const line = cart.items.findIndex((it) => it.variant_id === variantId);
        if (line >= 0) {
          await this._doCartAction('/cart/change.js', { line: line + 1, quantity: 0 });
        }
      } catch (e) { console.error(e); }
    }

    async _onGiftVariantChange(event) {
      const newId = parseInt(event.currentTarget.value, 10);
      if (!newId) return;
      // Just update the chosen variant id; the self-healing reconcile will
      // remove the old-variant gift line and add the new one (exactly one),
      // so we don't manually add/remove here (that caused duplicates).
      this.giftVariantId = String(newId);
      this.dataset.giftVariantId = this.giftVariantId;
      this._reconcileGiftSilent();
    }

    _onUpsellVariantChange(event) {
      const sel = event.currentTarget;
      const newId = parseInt(sel.value, 10);
      const wrap = sel.closest('.charm-upsell');
      const toggle = wrap && wrap.querySelector('[data-upsell-toggle]');
      if (!toggle) return;
      const oldId = parseInt(toggle.dataset.variantId, 10);
      toggle.dataset.variantId = String(newId);
      if (toggle.getAttribute('aria-pressed') === 'true' && oldId !== newId) {
        (async () => {
          try {
            const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' }})).json();
            const line = cart.items.findIndex((it) => it.variant_id === oldId);
            if (line >= 0) {
              await this._doCartAction('/cart/change.js', { line: line + 1, quantity: 0 }, { skipUpdate: true });
            }
            await this._doCartAction('/cart/add.js', { id: newId, quantity: 1 });
          } catch (e) { console.error(e); }
        })();
      }
    }
  }

  customElements.define('charm-cart', CharmCart);
})();
